import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { useAuth } from '../auth/AuthContext';
import { isCvOwner } from '../auth/RequireAuth';

// `section` items scroll to a home page section, `route` items navigate to a page.
type MenuItem = { label: string; section: string } | { label: string; route: string };

const menuItems: MenuItem[] = [
    { label: 'Home', section: 'home' },
    { label: 'Services', section: 'services' },
    { label: 'Hosting', route: '/hosting' },
    { label: 'Portfolio', section: 'portfolio' },
    { label: 'Contact', section: 'contact' }
];

export default function NavBarMobile({ goToSection }: { goToSection: (id: string) => void }) {
    const [open, setOpen] = useState(false);
    const overlayRef = useRef<HTMLDivElement>(null);
    const linksRef = useRef<Array<HTMLAnchorElement | null>>([]);
    const busyRef = useRef(false);
    const routerNavigate = useNavigate();
    const { user } = useAuth();
    const items: MenuItem[] = [
        ...menuItems,
        ...(isCvOwner(user) ? [{ label: 'CV', route: '/cv' }] : []),
        ...(user?.role === 'admin' ? [{ label: 'Admin', route: '/admin' }] : []),
        user ? { label: 'My account', route: '/account' } : { label: 'Log in', route: '/login' },
    ];

    const closeMenu = useCallback(() => {
        if (busyRef.current) return;
        busyRef.current = true;
        gsap.to(overlayRef.current, {
            opacity: 0, duration: 0.25, ease: 'power2.in',
            onComplete: () => {
                gsap.set(overlayRef.current, { display: 'none' });
                setOpen(false);
                busyRef.current = false;
            }
        });
    }, []);

    const navigate = useCallback((item: MenuItem) => {
        if ('route' in item) routerNavigate(item.route);
        else goToSection(item.section);
        closeMenu();
    }, [goToSection, routerNavigate, closeMenu]);

    const toggleMenu = useCallback(() => {
        if (busyRef.current) return;
        busyRef.current = true;

        if (!open) {
            setOpen(true);
            gsap.set(overlayRef.current, { display: 'flex' });
            gsap.fromTo(overlayRef.current,
                { opacity: 0 },
                { opacity: 1, duration: 0.35, ease: 'power2.out' }
            );
            gsap.fromTo(linksRef.current,
                { y: 40, opacity: 0 },
                {
                    y: 0, opacity: 1,
                    duration: 0.5, ease: 'power3.out',
                    stagger: 0.07, delay: 0.1,
                    onComplete: () => { busyRef.current = false; }
                }
            );
        } else {
            busyRef.current = false;
            closeMenu();
        }
    }, [open, closeMenu]);

    return (
        <>
            <header className="mobile-header">
                <a className="logo-link" onClick={() => goToSection('home')} style={{ cursor: 'pointer' }}>
                    <img src="/altwhite.png" alt="Forget-IT" className="logo-img" />
                </a>
                <button
                    className={`burger${open ? ' is-open' : ''}`}
                    onClick={toggleMenu}
                    aria-label={open ? 'Close menu' : 'Open menu'}
                    aria-expanded={open}
                >
                    <span className="burger-line" />
                    <span className="burger-line" />
                    <span className="burger-line" />
                </button>
            </header>

            <div className="mobile-overlay" ref={overlayRef} style={{ display: 'none' }}>
                <nav className="mobile-nav">
                    {items.map((item, i) => (
                        <a
                            key={item.label}
                            ref={el => { linksRef.current[i] = el; }}
                            className="mobile-nav-link"
                            onClick={() => navigate(item)}
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>
            </div>
        </>
    );
}